import { ANALYTICS_EVENTS, ORDER_TYPES, PAYMENT_METHODS, PAYMENT_TYPES } from '@nandosaus/constants';
import { compact, get } from 'lodash';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { analytics } from '@analytics';
import { formatSubCartsForProducts } from '@nandosaus/state-management/lib/util/analytics';
import { buildConfig as buildApplePayConfig, payWithApplePay } from '../../utils/apple-pay';
import { buildConfig as buildGooglePayConfig, payWithGooglePay } from '../../utils/google-pay';
import { clearPersistedCartData, persistCart } from '../../utils/data';
import {
  EXPRESS_CHECKOUT_FALLBACK_PARAM,
  EXPRESS_CHECKOUT_INCOMPLETE_DETAILS,
  applePayContactErrors,
  getInvalidGuestDetailFields,
  googlePayErrorMessage,
  guestDetailsFromApplePayContact,
  guestDetailsFromGooglePayData,
} from '../../utils/express-checkout';
import { logger } from '../../utils/logger';
import { navigation } from '../../constants';
import { useFeature } from '../use-feature';
import { useSchema } from '../use-schema';

const RECAPTCHA_REFRESH_INTERVAL = 1000 * 60; // reCAPTCHA v3 tokens expire after two minutes

const GOOGLE_PAY_CANCELLED = 'CANCELED';

/**
 * Drives the guest express checkout offered from the cart: works out which wallet the device
 * supports (Apple Pay first, then Google Pay), then pays with it and places the guest order using
 * the contact details the wallet returns — skipping the checkout page entirely.
 *
 * Wallet details are checked against the guest checkout schema inside the wallet sheet, so the
 * customer can fix a missing mobile or name there. Anything the express path still can't finish
 * on its own (invalid details that slipped through, a missing reCAPTCHA token, a 3DS challenge or
 * an order error) falls back to the regular guest checkout page, with whatever details the wallet
 * supplied prefilled.
 *
 * Must be rendered inside a GoogleReCaptchaProvider.
 */
const useExpressCheckout = () => {
  const rootStore = useRootStore();
  const { CartStore, GroupOrderStore, OrderingContextStore, OrderStore, PaymentStore } = rootStore;
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [walletError, setWalletError] = useState(null);
  // Set synchronously on tap, so a quick second tap can't start another wallet session while the
  // first sheet is open (Safari rejects a second ApplePaySession, which surfaced as a payment error).
  const isWalletInProgress = useRef(false);

  const enableApplePay = useFeature('pay-with-apple-pay');
  const enableGooglePay = useFeature('pay-with-google-pay');
  const { restaurantIds: restaurantIdsWithOptionalMobileNumber } = useFeature('remove-guest-mobile-requirement') || {
    restaurantIds: [],
  };
  const guestCheckoutSchema = useSchema('guestCheckout');

  // Wallet availability (PaymentStore.alternativePaymentMethods) is prefetched by CartPreview when
  // the cart opens, so the wallet buttons are ready by the time this sheet is shown.

  // Fetch the guest reCAPTCHA token ahead of time: the wallet sheet has to open synchronously from
  // the click, so there's no chance to await one once the customer taps the button.
  const { executeRecaptcha } = useGoogleReCaptcha();
  const recaptchaToken = useRef(null);
  useEffect(() => {
    if (!executeRecaptcha) {
      return undefined;
    }

    const refreshToken = () =>
      executeRecaptcha('guest_checkout')
        .then(token => {
          recaptchaToken.current = token;
        })
        .catch(error => logger.warn('express checkout recaptcha failed', { error: error?.message }));

    refreshToken();
    const timer = setInterval(refreshToken, RECAPTCHA_REFRESH_INTERVAL);

    return () => clearInterval(timer);
  }, [executeRecaptcha]);

  const { isApplePaySupported, isGooglePaySupported } = PaymentStore.alternativePaymentMethods;
  const hasNonZeroTotalPrice = CartStore.totalPrices.cents > 0;

  // Every wallet this device and restaurant support, in the order the design shows them.
  const walletPaymentTypes = useMemo(
    () =>
      hasNonZeroTotalPrice
        ? compact([
            enableApplePay && isApplePaySupported && PAYMENT_TYPES.APPLE_PAY,
            enableGooglePay && isGooglePaySupported && PAYMENT_TYPES.GOOGLE_PAY,
          ])
        : [],
    [enableApplePay, enableGooglePay, hasNonZeroTotalPrice, isApplePaySupported, isGooglePaySupported]
  );

  const isMobileNumberRequired =
    OrderingContextStore.orderType === ORDER_TYPES.DELIVERY ||
    !restaurantIdsWithOptionalMobileNumber.includes(OrderingContextStore.restaurantId);
  const validationSchema = useMemo(
    () => (isMobileNumberRequired ? guestCheckoutSchema : guestCheckoutSchema.omit(['mobile'])),
    [guestCheckoutSchema, isMobileNumberRequired]
  );

  const goToGuestCheckout = useCallback(
    (query = {}) => {
      CartStore.setIsGuestCheckoutFlow(true);
      router.push({ pathname: navigation.CHECKOUT, query });
    },
    [CartStore, router]
  );

  const continueAsGuest = useCallback(() => goToGuestCheckout(), [goToGuestCheckout]);

  // Field names only (never values), so we can see which details each wallet fails to supply.
  const logInvalidDetails = useCallback((message, walletPaymentType, invalidFields) => {
    logger.warn(message, { paymentType: walletPaymentType, invalidFields });
  }, []);

  const validateApplePayContact = useCallback(
    shippingContact => {
      const invalidFields = getInvalidGuestDetailFields(
        validationSchema,
        guestDetailsFromApplePayContact(shippingContact)
      );
      if (invalidFields.length > 0) {
        logInvalidDetails('express checkout wallet details rejected', PAYMENT_TYPES.APPLE_PAY, invalidFields);
      }
      return applePayContactErrors(invalidFields);
    },
    [logInvalidDetails, validationSchema]
  );

  const validateGooglePayData = useCallback(
    paymentData => {
      const invalidFields = getInvalidGuestDetailFields(validationSchema, guestDetailsFromGooglePayData(paymentData));
      if (invalidFields.length > 0) {
        logInvalidDetails('express checkout wallet details rejected', PAYMENT_TYPES.GOOGLE_PAY, invalidFields);
      }
      return googlePayErrorMessage(invalidFields);
    },
    [logInvalidDetails, validationSchema]
  );

  const placeOrder = useCallback(
    async ({ walletPaymentType, token, guestDetails }) => {
      // Cancelled from the wallet sheet.
      if (!token) {
        return;
      }

      CartStore.setGuestCheckoutCustomerDetails({ ...guestDetails, token: recaptchaToken.current || undefined });

      // Normally caught inside the wallet sheet; this is the backstop.
      const invalidFields = getInvalidGuestDetailFields(validationSchema, guestDetails);
      if (invalidFields.length > 0) {
        logInvalidDetails('express checkout falling back to guest checkout', walletPaymentType, invalidFields);
        goToGuestCheckout({ [EXPRESS_CHECKOUT_FALLBACK_PARAM]: EXPRESS_CHECKOUT_INCOMPLETE_DETAILS });
        return;
      }

      if (!recaptchaToken.current) {
        logger.warn('express checkout falling back to guest checkout', {
          paymentType: walletPaymentType,
          reason: 'missing-recaptcha-token',
        });
        goToGuestCheckout();
        return;
      }

      // Each reCAPTCHA token can only be verified once.
      recaptchaToken.current = null;

      CartStore.payment.setAlternativePaymentMethod({ type: PAYMENT_METHODS[walletPaymentType], token });
      persistCart(rootStore);
      setIsSubmitting(true);

      const { submitted } = await CartStore.createOrder();

      if (submitted) {
        clearPersistedCartData();
        router.push(navigation.CHECKOUT_SUCCESS);
        return;
      }

      // The checkout page owns 3DS challenges, the reCAPTCHA v2 fallback and order error modals.
      logger.warn('express checkout falling back to guest checkout', {
        paymentType: walletPaymentType,
        reason: 'order-not-submitted',
        orderStatus: OrderStore.status,
      });
      setIsSubmitting(false);
      goToGuestCheckout();
    },
    [CartStore, OrderStore, goToGuestCheckout, logInvalidDetails, rootStore, router, validationSchema]
  );

  const handleWalletError = useCallback(
    (walletPaymentType, error) => {
      CartStore.setIsGuestCheckoutFlow(false);

      // Apple Pay cancellations resolve without a token; Google Pay's reject with CANCELED.
      if (get(error, 'statusCode') === GOOGLE_PAY_CANCELLED) {
        return;
      }

      logger.warn('express checkout wallet payment failed', {
        paymentType: walletPaymentType,
        error: error?.statusMessage || error?.message,
      });
      // The cart has no checkout error modals, so the sheet shows this inline.
      setWalletError('Something went wrong with your payment. Please try again or continue as guest.');
    },
    [CartStore]
  );

  // NOTE: Everything up to the wallet call must stay synchronous. Safari only allows an
  // ApplePaySession to be created inside the click's user gesture, and a promise or `await` before
  // it breaks that (see guest-order-checkout).
  const payWithWallet = useCallback(
    walletPaymentType => {
      if (!walletPaymentTypes.includes(walletPaymentType) || isSubmitting || isWalletInProgress.current) {
        return undefined;
      }

      isWalletInProgress.current = true;
      setWalletError(null);
      CartStore.setIsGuestCheckoutFlow(true);
      CartStore.setSplitPaymentType(walletPaymentType);

      analytics.track(ANALYTICS_EVENTS.CHECKOUT_STARTED, {
        total: CartStore.totalPrices.dollarValue,
        coupon: CartStore.offer ? CartStore.offer.description : undefined,
        products: formatSubCartsForProducts(CartStore.subCarts, {
          orderType: CartStore.orderType,
          group: GroupOrderStore.group,
        }),
      });
      CartStore.setFunnelTimestamp('begin_checkout');

      const walletPayment =
        walletPaymentType === PAYMENT_TYPES.APPLE_PAY
          ? payWithApplePay(
              buildApplePayConfig({
                orderSummary: OrderStore.orderSummaryForApplePayWeb,
                allowedCardNetworks: PaymentStore.availableCreditCardTypesForApplePay,
                requestContactDetails: true,
              }),
              { validateShippingContact: validateApplePayContact }
            ).then(({ token, shippingContact }) => ({
              walletPaymentType,
              token,
              guestDetails: guestDetailsFromApplePayContact(shippingContact),
            }))
          : payWithGooglePay(
              buildGooglePayConfig({
                orderSummary: OrderStore.orderSummaryForGooglePay,
                allowedCardNetworks: PaymentStore.availableCreditCardTypesForGooglePay,
                requestContactDetails: true,
              }),
              { validatePaymentData: validateGooglePayData }
            ).then(paymentData => ({
              walletPaymentType,
              token: get(paymentData, 'paymentMethodData.tokenizationData.token'),
              guestDetails: guestDetailsFromGooglePayData(paymentData),
            }));

      return walletPayment
        .then(placeOrder, error => handleWalletError(walletPaymentType, error))
        .finally(() => {
          isWalletInProgress.current = false;
        });
    },
    [
      CartStore,
      GroupOrderStore,
      OrderStore,
      PaymentStore,
      handleWalletError,
      isSubmitting,
      placeOrder,
      validateApplePayContact,
      validateGooglePayData,
      walletPaymentTypes,
    ]
  );

  return {
    walletPaymentTypes,
    isLoadingPaymentMethods: PaymentStore.loading,
    isSubmitting,
    walletError,
    payWithWallet,
    continueAsGuest,
  };
};

export { useExpressCheckout };
